(() => {
  if (window.AstrellaCollection) return;

  // This scene has its own visibility lifecycle: the hero is usually offscreen
  // by the time the collection comes into view.
  function create({element, signal, reduced, finePointer}) {
    const canvas = element.querySelector('.fx-coming-canvas');
    let ctx;
    try { ctx = canvas?.getContext('2d'); } catch { return null; }
    if (!ctx || !window.AstrellaOrb) return null;

    const on = (target, event, callback, options = {}) => target.addEventListener(event, callback, {...options, signal});
    let width = 0, height = 0, radius = 0, renderer = null, frame = 0, previous = null, phase = 0;
    let visible = !('IntersectionObserver' in window), disposed = false;
    const pointer = {x: 0, y: 0, targetX: 0, targetY: 0, filteredX: 0, filteredY: 0, velocityX: 0, velocityY: 0};
    const samples = Array.from({length: 73}, (_, i) => ({c: Math.cos(i / 72 * Math.PI), s: Math.sin(i / 72 * Math.PI)}));
    const stars = Array.from({length: 30}, (_, i) => ({x: ((i * 173 + 41) % 997) / 997, y: ((i * 367 + 113) % 991) / 991, opacity: .06 + (i % 4) * .025}));
    const glow = document.createElement('canvas');
    glow.width = glow.height = 128;
    const glowContext = glow.getContext('2d');
    if (glowContext) {
      const light = glowContext.createRadialGradient(64, 64, 1, 64, 64, 64);
      light.addColorStop(0, '#eed4a6a0');
      light.addColorStop(.13, '#d8ab6260');
      light.addColorStop(.4, '#b87b301c');
      light.addColorStop(1, '#b87b3000');
      glowContext.fillStyle = light;
      glowContext.fillRect(0, 0, 128, 128);
    }

    function draw() {
      if (!width || !height || disposed) return;
      // Allocate the GPU surface only when this artwork is first seen.
      renderer ||= window.AstrellaOrb.create({size: 640});
      ctx.clearRect(0, 0, width, height);
      const cx = width * .5 + pointer.x * 12 + Math.sin(phase * .24) * 3;
      const cy = height * .465 + pointer.y * 9 + Math.sin(phase * .3) * 3;
      for (const star of stars) {
        ctx.fillStyle = `rgba(223,198,158,${star.opacity * (.8 + .2 * Math.sin(phase * .6 + star.x * 15))})`;
        ctx.beginPath();
        ctx.arc(star.x * width, star.y * (height - 52), .6, 0, Math.PI * 2);
        ctx.fill();
      }
      const orbits = [
        {r: radius * 1.48, squash: .43 + Math.sin(phase * .16) * .07, tilt: -.48 + Math.sin(phase * .14) * .26 + pointer.x * .08, angle: phase * .36 + 2.3, alpha: .3, bead: 2.7},
        {r: radius * 1.64, squash: .58 + Math.sin(phase * .13 + 1) * .05, tilt: .48 + Math.sin(phase * .12 + .5) * .22 - pointer.y * .08, angle: -phase * .23 + .5, alpha: .16, bead: 1.5}
      ];
      const point = (orbit, c, s) => ({
        x: cx + orbit.r * (c * Math.cos(orbit.tilt) - s * orbit.squash * Math.sin(orbit.tilt)),
        y: cy + orbit.r * (c * Math.sin(orbit.tilt) + s * orbit.squash * Math.cos(orbit.tilt))
      });
      const strokeOrbit = (orbit, front) => {
        ctx.beginPath();
        samples.forEach((sample, i) => {
          const p = point(orbit, sample.c * (front ? 1 : -1), sample.s * (front ? 1 : -1));
          if (i) ctx.lineTo(p.x, p.y); else ctx.moveTo(p.x, p.y);
        });
        ctx.lineWidth = .75;
        ctx.strokeStyle = `rgba(221,196,153,${orbit.alpha * (front ? 1 : .52)})`;
        ctx.stroke();
      };
      const drawCompanion = (orbit, front) => {
        if ((Math.sin(orbit.angle) >= 0) !== front) return;
        // A short, tapered glint follows the companion along its actual orbit.
        const direction = orbit === orbits[0] ? 1 : -1;
        for (let i = 0; i < 14; i++) {
          const a = orbit.angle - direction * (.23 - i * .016);
          if ((Math.sin(a) >= 0) !== front) continue;
          const p = point(orbit, Math.cos(a), Math.sin(a));
          const q = point(orbit, Math.cos(a + direction * .016), Math.sin(a + direction * .016));
          ctx.strokeStyle = `rgba(235,207,164,${.025 + i * .016})`;
          ctx.beginPath();ctx.moveTo(p.x, p.y);ctx.lineTo(q.x, q.y);ctx.stroke();
        }
        const p = point(orbit, Math.cos(orbit.angle), Math.sin(orbit.angle));
        const size = orbit.bead * 20;
        if (glowContext) ctx.drawImage(glow, p.x - size / 2, p.y - size / 2, size, size);
        ctx.fillStyle = '#e7cea4';ctx.beginPath();ctx.arc(p.x, p.y, orbit.bead, 0, Math.PI * 2);ctx.fill();
        ctx.fillStyle = '#fff0d4';ctx.beginPath();ctx.arc(p.x - orbit.bead * .2, p.y - orbit.bead * .25, orbit.bead * .38, 0, Math.PI * 2);ctx.fill();
      };
      // Back trajectories pass behind the sphere; the near halves cross its
      // surface. Occlusion gives the illustration depth as the companions move.
      orbits.forEach(orbit => { strokeOrbit(orbit, false); drawCompanion(orbit, false); });
      const size = radius / .3;
      ctx.drawImage(renderer.render(phase, pointer.x, pointer.y), cx - size / 2, cy - size / 2, size, size);
      orbits.forEach(orbit => { strokeOrbit(orbit, true); drawCompanion(orbit, true); });
      element.classList.add('has-live-art');
    }

    function stop() { cancelAnimationFrame(frame); frame = 0; previous = null; }
    const canRun = () => !disposed && visible && !reduced.matches && !document.hidden && element.isConnected && width > 0 && height > 0;
    function start() { if (!frame && canRun()) frame = requestAnimationFrame(tick); }
    function tick(time) {
      frame = 0;
      if (!canRun()) { previous = null; return; }
      const interval = 1000 / (finePointer.matches ? 60 : 30);
      if (previous === null) previous = time - interval;
      const elapsed = time - previous;
      if (elapsed >= interval - .75) {
        const dt = Math.min(elapsed, 50) / 1000;
        previous = time;phase += dt;
        const filter = 1 - Math.exp(-dt / .18), spring = 4.2, decay = Math.exp(-spring * dt);
        for (const axis of ['X', 'Y']) {
          const position = axis.toLowerCase(), filtered = 'filtered' + axis, velocity = 'velocity' + axis;
          pointer[filtered] += (pointer['target' + axis] - pointer[filtered]) * filter;
          const offset = pointer[position] - pointer[filtered], step = (pointer[velocity] + spring * offset) * dt;
          pointer[position] = pointer[filtered] + (offset + step) * decay;
          pointer[velocity] = (pointer[velocity] - spring * step) * decay;
        }
        draw();
      }
      frame = requestAnimationFrame(tick);
    }
    function sync() {
      stop();
      if (reduced.matches) Object.keys(pointer).forEach(key => { pointer[key] = 0; });
      if (visible && !document.hidden) draw();
      start();
    }
    function resize() {
      const bounds = canvas.getBoundingClientRect();
      if (width === bounds.width && height === bounds.height) return;
      width = bounds.width;height = bounds.height;
      radius = Math.min(width * .28, height * .33);
      const density = Math.min(devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * density);canvas.height = Math.round(height * density);
      ctx.setTransform(density, 0, 0, density, 0, 0);ctx.imageSmoothingQuality = 'high';
      sync();
    }
    on(element, 'pointermove', event => {
      if (reduced.matches || !finePointer.matches || event.pointerType === 'touch' || !width || !height) return;
      const bounds = canvas.getBoundingClientRect();
      pointer.targetX = Math.max(-.5, Math.min(.5, (event.clientX - bounds.left) / width - .5));
      pointer.targetY = Math.max(-.5, Math.min(.5, (event.clientY - bounds.top) / height - .5));
    }, {passive: true});
    on(element, 'pointerleave', () => { pointer.targetX = pointer.targetY = 0; });
    on(reduced, 'change', sync);
    on(document, 'visibilitychange', sync);
    const visibilityObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => { visible = entries[0].isIntersecting;sync(); }) : null;
    const sizeObserver = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
    resize();visibilityObserver?.observe(element);sizeObserver?.observe(element);
    if (!sizeObserver) on(window, 'resize', resize);
    return {dispose() { disposed = true;stop();visibilityObserver?.disconnect();sizeObserver?.disconnect();renderer?.dispose();element.classList.remove('has-live-art'); }};
  }
  window.AstrellaCollection = {create};
})();
